/**
 * Dialog for triggering a workflow suite run with optional overrides.
 *
 * @module pages/RunSuiteDialog
 * @see docs/api/services/benchmark.md
 */
import React from 'react';
import { Button, Dialog, DialogTrigger, DialogFooter, DialogContent, Select, Text } from '@chatsdet/ui';
import { Play } from '@phosphor-icons/react';
import {
  useBenchmarkConfigsQuery,
  useTriggerWorkflowSuiteMutation,
  type BenchmarkConfig,
} from '../useBenchmarkData';
import { useProviderModelOptions, modelOptionsForProvider } from '../useAiModelCatalog';

export function RunSuiteDialog() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedConfigId, setSelectedConfigId] = React.useState<string | null>(null);
  const [providerOverride, setProviderOverride] = React.useState('');
  const [modelOverride, setModelOverride] = React.useState('');

  const configsQuery = useBenchmarkConfigsQuery({ jobType: 'workflow_suite' });
  const triggerMutation = useTriggerWorkflowSuiteMutation();
  const { catalog, catalogQuery, providerOptions, modelOptions } = useProviderModelOptions(
    providerOverride,
    { providerDefault: 'Use config default', modelDefault: 'Use config default' }
  );

  const configs = configsQuery.data?.configs ?? [];

  const configOptions = React.useMemo(
    () =>
      configs.map((c: BenchmarkConfig) => ({
        id: c.id,
        label: `${c.name}${c.isDefault ? ' (default)' : ''}`,
      })),
    [configs]
  );

  const selectedConfig = React.useMemo(
    () => configs.find((c: BenchmarkConfig) => c.id === selectedConfigId) ?? null,
    [configs, selectedConfigId]
  );

  // Auto-select default config on load
  React.useEffect(() => {
    if (configs.length > 0 && !selectedConfigId) {
      const defaultConfig = configs.find((c: BenchmarkConfig) => c.isDefault);
      setSelectedConfigId(defaultConfig?.id ?? configs[0].id);
    }
  }, [configs, selectedConfigId]);

  const handleRun = () => {
    if (!selectedConfigId) return;
    triggerMutation.mutate({
      configId: selectedConfigId,
      providerOverride: providerOverride || undefined,
      modelOverride: modelOverride || undefined,
    });
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setProviderOverride('');
      setModelOverride('');
      triggerMutation.reset();
    }
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange} size="md">
      <Button isDisabled={configs.length === 0} iconLeft={<Play size={16} />}>
        Run Suite
      </Button>
      <Dialog
        title="Run Workflow Suite"
        description="Select a configuration and optionally override AI settings for this run."
      >
        <DialogContent>
          <Select
            label="Configuration"
            placeholder="Select a config"
            items={configOptions}
            selectedKey={selectedConfigId ?? undefined}
            onSelectionChange={(key) => setSelectedConfigId(key ? String(key) : null)}
          />

          {selectedConfig && (
            <div className="bg-muted/30 rounded-md p-3 space-y-1 text-sm">
              {selectedConfig.provider && (
                <Text size="sm">
                  Provider: <span className="font-medium">{selectedConfig.provider}</span>
                  {selectedConfig.model && <> / {selectedConfig.model}</>}
                </Text>
              )}
              {selectedConfig.description && (
                <Text size="xs" className="text-muted-foreground">
                  {selectedConfig.description}
                </Text>
              )}
            </div>
          )}

          <div className="border-t border-border pt-3 space-y-3">
            <Text size="sm" weight="medium" className="text-muted-foreground">
              Overrides (optional)
            </Text>
            <Select
              label="Provider"
              items={providerOptions}
              selectedKey={providerOverride}
              onSelectionChange={(key) => {
                const provider = String(key ?? '');
                const allowed = modelOptionsForProvider(catalog, provider || null);
                setProviderOverride(provider);
                // Drop a model the newly chosen provider can't serve.
                if (modelOverride && !allowed.includes(modelOverride)) setModelOverride('');
              }}
            />
            <Select
              label="Model"
              placeholder={catalogQuery.isLoading ? 'Loading…' : 'Use config default'}
              items={modelOptions}
              selectedKey={modelOverride}
              isDisabled={catalogQuery.isLoading}
              onSelectionChange={(key) => setModelOverride(String(key ?? ''))}
            />
          </div>

          {triggerMutation.isError && (
            <Text className="text-destructive">{triggerMutation.error.message}</Text>
          )}

          {triggerMutation.isSuccess && (
            <div className="rounded-md border border-success/30 bg-success/10 px-4 py-2">
              <Text className="text-success">
                Suite job queued. Job ID: {triggerMutation.data.jobId}
              </Text>
            </div>
          )}
        </DialogContent>

        <DialogFooter>
          <Button variant="outline" onPress={() => handleOpenChange(false)}>
            {triggerMutation.isSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!triggerMutation.isSuccess && (
            <Button
              onPress={handleRun}
              isDisabled={!selectedConfigId || triggerMutation.isPending}
              iconLeft={<Play size={16} />}
            >
              {triggerMutation.isPending ? 'Starting...' : 'Run'}
            </Button>
          )}
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
